'use client';
import type { ProjectionHorizon } from '@/lib/projectScore';
import { HomeTimeSlider } from './HomeTimeSlider';
import { AlertsToggle } from '@/components/AlertsToggle';

type TierFilter = 'all' | 'alpha' | 'top10q' | 'top20q' | 'top15q' | 'combined';
export type VMode = 'classic' | 'blended';

interface Props {
  /** Top-right: monthly time-travel slider (replaces the old period-tab row). */
  historicalMonth: string | null;
  historicalLoading: boolean;
  onHistoricalMonthChange?: (month: string | null) => void;

  /** Tier filter group: Alpha Zone / Top 10 Q / Top 20 Q. */
  tierFilter: TierFilter;
  onTierFilterChange: (next: TierFilter) => void;

  /** Forward projection horizon — driven by the same slider. */
  projectionHorizon?: ProjectionHorizon;
  onProjectionChange?: (h: ProjectionHorizon) => void;

  /** V axis mode toggle — Classic = peer-z V (legacy), Blended = production
   *  (0.75·peer-z + 0.25·universe-z absolute). When provided the overlay
   *  renders a Classic/Blended pill alongside Top 15 Q. Default behavior
   *  (no prop) hides the toggle. */
  vMode?: VMode;
  onVModeChange?: (mode: VMode) => void;

  /** Tier-filter button set. Defaults to a single Top 15 Q pill (universe
   *  context). Portfolio Visualizer overrides with `[{ key: 'combined',
   *  label: 'Combined' }]` because Top 15 Q is meaningless on a 17-stock
   *  book — Steve directive 2026-05-22. */
  tierButtons?: ReadonlyArray<{ key: TierFilter; label: string; title?: string }>;
}

const DEFAULT_TIER_BUTTONS: ReadonlyArray<{ key: TierFilter; label: string; title?: string }> = [
  { key: 'top15q', label: 'Top 15 Q' },
];

/**
 * Top-right control stack on the 3D scatter:
 * (1) unified time slider (history → now → projection),
 * (2) tier filter (Alpha/Top10Q/Top20Q).
 */
export function ScatterControlsOverlay({
  historicalMonth,
  historicalLoading,
  onHistoricalMonthChange,
  tierFilter,
  onTierFilterChange,
  projectionHorizon,
  onProjectionChange,
  vMode,
  onVModeChange,
  tierButtons,
}: Props) {
  const buttons = tierButtons ?? DEFAULT_TIER_BUTTONS;
  return (
    <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-2">
      {onHistoricalMonthChange && onProjectionChange && (
        <HomeTimeSlider
          historicalMonth={historicalMonth}
          projectionHorizon={projectionHorizon ?? 'now'}
          onHistoricalMonthChange={onHistoricalMonthChange}
          onProjectionChange={onProjectionChange}
          loading={historicalLoading}
        />
      )}

      <div className="flex gap-1 items-center">
        {vMode != null && onVModeChange && (
          <div className="flex gap-1 mr-2 bg-[#0a0e14] border border-[#1e293b] rounded-md p-0.5">
            <button
              onClick={() => onVModeChange('classic')}
              className={`text-[10px] font-semibold tracking-wide uppercase transition-colors px-2 py-1 rounded ${
                vMode === 'classic'
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                  : 'text-[#94a3b8] hover:text-white border border-transparent'
              }`}
              title="V = peer-z multiples only (legacy)"
            >
              Classic V
            </button>
            <button
              onClick={() => onVModeChange('blended')}
              className={`text-[10px] font-semibold tracking-wide uppercase transition-colors px-2 py-1 rounded ${
                vMode === 'blended'
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                  : 'text-[#94a3b8] hover:text-white border border-transparent'
              }`}
              title="V = 75% peer-z + 25% universe-z absolute (production)"
            >
              Blended V
            </button>
          </div>
        )}
        {buttons.map(({ key, label, title }) => {
          const isActive = tierFilter === key;
          return (
            <button
              key={key}
              onClick={() => onTierFilterChange(isActive ? 'all' : key)}
              title={title}
              className={`text-[11px] font-semibold tracking-wide transition-all duration-150 cursor-pointer rounded-md px-3.5 py-1.5 ${
                isActive
                  ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                  : 'bg-[#111827] text-[#94a3b8] border border-[#1e293b] hover:text-white hover:border-[#334155]'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Master kill switch for the Action Monitor feature. Steve directive
          2026-05-23: place directly under the Top 15 Q button so it's
          glanceable and easy to flip off if the alerts are noisy.
          Persists to localStorage; affects only UI visibility, the cron
          keeps populating data/action-alerts.jsonl regardless. */}
      <AlertsToggle />
    </div>
  );
}
